import { isPast } from 'date-fns'
import type {
  Session,
  LoginFlow,
  LogoutFlow,
  RegistrationFlow,
  UiNodeInputAttributes,
  SuccessfulNativeRegistration,
  VerificationFlow,
  RecoveryFlow,
  SettingsFlow,
  ContinueWithVerificationUi,
  UiNode,
} from '@ory/client'
import { FrontendApi, Configuration } from '@ory/client'
import type { AxiosError } from 'axios'
import { isAxiosError } from 'axios'

export const useAuthStore = defineStore('auth', () => {
  const runtimeConfig = useRuntimeConfig()
  const orySessionCookie = useCookie(runtimeConfig.public.ory.sessionCookieName)
  const orySessionCookieAvailable = useState(
    'ory-session-cookie-available',
    () => (orySessionCookie.value ? true : false),
  )
  const confData = useConfdataStore()
  const partner = usePartnerStore()
  const authFlow = useAuthFlow()
  const { lastUsed } = useWishlist()

  const ory = new FrontendApi(
    new Configuration({
      basePath: getOryAPIUrl(),
      baseOptions: {
        // Ensures we send cookies and all other headers in the CORS requests.
        withCredentials: true,
      },
    }),
  )

  const pending = ref(false)
  const session = ref<Session>()
  const csrfToken = ref<string>()
  const email = ref<string>()
  const registerFlow = ref<
    RegistrationFlow | ContinueWithVerificationUi['flow'] | VerificationFlow
  >()
  const loginFlow = ref<LoginFlow>()
  const recoverFlow = ref<RecoveryFlow>()
  const logoutFlow = ref<LogoutFlow>()

  const isSessionExpired = computed(
    () =>
      !session.value ||
      isPast(tryParseDate(session.value.expires_at) ?? new Date()),
  )
  const isAuthorized = computed(() => !isSessionExpired.value)
  const isRegistrationStarted = computed(() => !!registerFlow.value)
  const isRecoveryStarted = computed(() => !!recoverFlow.value)

  async function init(skipSessionCheck: boolean = false) {
    // To traffic and latency, Ory asked not to call the `/whoami` endpoint if the session cookie is missing.
    // See https://jira.migros.net/browse/IHGWEBCC-1632
    if (!skipSessionCheck && !orySessionCookieAvailable.value) return

    try {
      pending.value = true
      session.value = (await ory.toSession(useRequestHeaders(['cookie']))).data
      logoutFlow.value = (await ory.createBrowserLogoutFlow()).data
    } catch (error) {
      if (!isAxiosError(error) || error.response?.status !== 401)
        console.error('Authentication check failed', error)
    } finally {
      pending.value = false
    }
  }

  async function registerStart(returnToPath: string) {
    const path = authFlow.createTargetPath(returnToPath)

    const { data } = await ory.createBrowserRegistrationFlow({
      returnTo: getReturnToCallbackUrl(path),
    })

    registerFlow.value = data
    csrfToken.value = getRegistrationCSRFToken(data)
  }

  async function registerWithPassword(identifier: string, password: string) {
    if (!registerFlow.value) throw new AuthError('noRegisterFlowError')

    try {
      const { data } = await ory.updateRegistrationFlow({
        flow: registerFlow.value.id,
        updateRegistrationFlowBody: {
          csrf_token: csrfToken.value,
          method: 'password',
          password,
          traits: { email: identifier, lang: confData.language },
        },
      })

      const continueWith = getContinueWithVerificationUi(data)

      if (!continueWith) throw new AuthError('unknownError')

      registerFlow.value = continueWith.flow
      session.value = data.session
      email.value = identifier
    } catch (error) {
      if (isInUseByAnotherError(error)) throw new AuthError('alreadyUsedError')
      if (isPasswordError(error)) throw new AuthError('databreachError')
      throw error
    }
  }

  async function registerWithProvider(provider: SocialProvider) {
    if (!registerFlow.value) throw new AuthError('noRegisterFlowError')

    try {
      const { data } = await ory.updateRegistrationFlow({
        flow: registerFlow.value.id,
        updateRegistrationFlowBody: {
          csrf_token: csrfToken.value,
          method: 'oidc',
          provider,
          traits: { lang: confData.language },
        },
      })

      session.value = data.session
    } catch (error) {
      if (isRedirectError(error))
        throw new RedirectAuthError(error.response?.data?.redirect_browser_to)
      throw new AuthError('initError')
    }
  }

  async function verifyStart() {
    const { data } = await ory.createBrowserVerificationFlow()

    registerFlow.value = data
    csrfToken.value = getRegistrationCSRFToken(data)
  }

  async function verifyComplete(params: { code?: string; email?: string }) {
    if (!registerFlow.value) throw new AuthError('noRegisterFlowError')

    const { data } = await ory.updateVerificationFlow({
      flow: registerFlow.value.id,
      updateVerificationFlowBody: {
        csrf_token: csrfToken.value,
        method: 'code',
        ...params,
      },
    })

    if (data.state === 'passed_challenge') return

    const [{ text }] = data.ui.messages ?? []
    if (text.includes('expired')) throw new AuthError('expiredCode')
    if (text.includes('invalid')) throw new AuthError('invalidCode')
  }

  async function loginStart(returnToPath: string) {
    const path = authFlow.createTargetPath(returnToPath)
    try {
      const { data } = await ory.createBrowserLoginFlow({
        returnTo: getReturnToCallbackUrl(path),
        refresh: false,
      })

      loginFlow.value = data
      csrfToken.value = getRegistrationCSRFToken(data)
    } catch (error) {
      if (isLoggedInOnAnotherDeviceError(error)) {
        await init(true)
        authFlow.toggle(false)
      }
    }
  }

  async function loginWithPassword(identifier: string, password: string) {
    if (!loginFlow.value) throw new AuthError('noLoginFlowError')

    try {
      const { data } = await ory.updateLoginFlow({
        flow: loginFlow.value.id,
        updateLoginFlowBody: {
          csrf_token: csrfToken.value,
          method: 'password',
          identifier,
          password,
        },
      })

      session.value = data.session
    } catch (error) {
      if (isLoggedInOnAnotherDeviceError(error)) return
      throw new AuthError('loginError')
    }
  }

  async function loginWithProvider(provider: SocialProvider) {
    if (!loginFlow.value) throw new AuthError('noLoginFlowError')

    try {
      const { data } = await ory.updateLoginFlow({
        flow: loginFlow.value.id,
        updateLoginFlowBody: {
          csrf_token: csrfToken.value,
          method: 'oidc',
          provider,
          traits: { lang: confData.language },
        },
      })

      session.value = data.session
    } catch (error) {
      if (isRedirectError(error))
        throw new RedirectAuthError(error.response?.data?.redirect_browser_to)
      throw new AuthError('initError')
    }
  }

  async function recoverStart() {
    try {
      const { data } = await ory.createBrowserRecoveryFlow()

      recoverFlow.value = data
      csrfToken.value = getRegistrationCSRFToken(data)
    } catch (error) {
      if (isSessionAlreadyAvailableError(error))
        throw new SessionExistsAuthError()
      throw error
    }
  }

  async function recoverComplete(params: { code?: string; email?: string }) {
    if (!recoverFlow.value) throw new AuthError('noRecoverFlowError')

    try {
      const { data } = await ory.updateRecoveryFlow({
        flow: recoverFlow.value.id,
        updateRecoveryFlowBody: {
          csrf_token: csrfToken.value,
          method: 'code',
          ...params,
          transient_payload: { lang: confData.language },
        },
      })

      data.return_to = 'resetpassword'
      recoverFlow.value = data
      email.value = params.email

      if (data.state !== 'sent_email') return

      const [{ text }] = data.ui.messages ?? []
      if (text.includes('expired')) throw new AuthError('expiredCode')
      if (text.includes('invalid')) throw new AuthError('invalidCode')
    } catch (error) {
      if (isAuthError(error)) throw error
      if (isRedirectNeededError(error)) return
      if (isSessionAlreadyAvailableError(error))
        throw new SessionExistsAuthError()
      throw error
    }
  }

  async function settingsStart() {
    const { data } = await ory.createBrowserSettingsFlow()

    data.return_to = 'login'
    recoverFlow.value = data
    csrfToken.value = getRegistrationCSRFToken(data)
  }

  async function settingsComplete(password: string) {
    if (!recoverFlow.value) throw new AuthError('noRecoverFlowError')

    try {
      const { data } = await ory.updateSettingsFlow({
        flow: recoverFlow.value.id,
        updateSettingsFlowBody: {
          csrf_token: csrfToken.value,
          method: 'password',
          password,
        },
      })

      if (data.state !== 'success') throw new AuthError('resetError')
    } catch (error) {
      if (isSessionRefreshRequiredError(error))
        throw new SessionRefreshRequiredError()
      throw error
    }
  }

  async function logout() {
    if (!logoutFlow.value) return

    try {
      pending.value = true
      await ory.updateLogoutFlow({ token: logoutFlow.value.logout_token })
      session.value = undefined
      lastUsed.value = undefined
      await updateBookmarks()
      authFlow.updateState({ isLoggedOut: true })
    } catch (error) {
      console.error('Logout failed', error)
    } finally {
      pending.value = false
    }
  }

  function watchLogoutStart() {
    if (!import.meta.client) return

    window.addEventListener('storage', logoutHandler)
  }

  function watchLogoutStop() {
    window.removeEventListener('storage', logoutHandler)
  }

  function logoutHandler(event: StorageEvent) {
    if (event.key !== 'authState') return

    const state: AuthState = JSON.parse(event.newValue as string)
    if (state && state.isLoggedOut && document.visibilityState !== 'visible') {
      session.value = undefined
    }
  }

  function getOryAPIUrl() {
    const { hostname } = useRequestURL()

    const routes = {
      localhost: 'http://localhost:4000', // Ory tunnel
      'webcc.hpk8s.com': 'https://ory-auth.webcc.hpk8s.com',
      'interhome.hpk8s.com': 'https://ory-auth.interhome.hpk8s.com',
      '*': `https://ory-auth.${hostname.replace(/^www\./, '')}`,
    }

    return (
      Object.entries(routes).find(([pattern]) =>
        hostname.includes(pattern),
      )?.[1] ?? routes['*']
    )
  }

  async function updateBookmarks() {
    if (!partner.id) {
      useBookmarksStore().clear()
    }
  }

  function getReturnToCallbackUrl(path: string) {
    const { origin } = useRequestURL()
    const url = new URL(path, origin).toString()
    return `${origin}/callback?returnTo=${url}`
  }

  return {
    email,
    pending,
    session,
    isAuthorized,
    isSessionExpired,
    isRegistrationStarted,
    isRecoveryStarted,
    init,
    registerStart,
    registerWithPassword,
    registerWithProvider,
    verifyStart,
    verifyComplete,
    loginStart,
    loginWithPassword,
    loginWithProvider,
    recoverStart,
    recoverComplete,
    settingsStart,
    settingsComplete,
    logout,
    watchLogoutStart,
    watchLogoutStop,
  }
})

function getRegistrationCSRFToken(
  flow: LoginFlow | RecoveryFlow | VerificationFlow | SettingsFlow,
): string | undefined {
  return flow?.ui?.nodes
    ?.filter(({ type }) => type === 'input')
    ?.map(({ attributes }) => attributes as UiNodeInputAttributes)
    ?.find((attributes) => attributes.name === 'csrf_token')?.value
}

function getContinueWithVerificationUi(
  data: SuccessfulNativeRegistration,
): ContinueWithVerificationUi | undefined {
  return data.continue_with?.find(
    ({ action }) => action === 'show_verification_ui',
  ) as ContinueWithVerificationUi | undefined
}

function isInUseByAnotherError(error: unknown): error is AxiosError {
  if (!isAxiosError(error)) return false

  return !!error.response?.data?.ui?.messages?.[0]?.text?.includes(
    'which is already in use by another account',
  )
}

function isPasswordError(error: unknown): error is AxiosError {
  if (!isAxiosError(error)) return false

  return !!error.response?.data?.ui?.nodes?.find(
    (node: UiNode) =>
      (node.attributes as UiNodeInputAttributes).name === 'password',
  ).messages?.[0]
}

function isRedirectNeededError(error: unknown): error is AxiosError {
  return isAxiosError(error) && error.response?.status === 422
}

function isSessionAlreadyAvailableError(error: unknown): error is AxiosError {
  if (!isAxiosError(error)) return false

  return error?.response?.data?.error?.id === 'session_already_available'
}

function isLoggedInOnAnotherDeviceError(error: unknown): error is AxiosError {
  if (!isAxiosError(error) || error.response?.status !== 400) return false

  return !!error.response?.data?.error?.reason.includes(
    'valid session was detected',
  )
}

function isRedirectError(error: unknown): error is AxiosError<any> {
  if (!isAxiosError(error)) return false

  return !!error.response?.data?.redirect_browser_to
}

function isSessionRefreshRequiredError(error: unknown): error is AxiosError {
  if (!isAxiosError(error)) return false

  return error.response?.data?.error?.id === 'session_refresh_required'
}

export class AuthError extends Error {
  constructor(public key: string) {
    super(key)
  }
}

export class RedirectAuthError extends AuthError {
  constructor(public redirectTo: string) {
    super('redirectError')
  }
}

export class SessionExistsAuthError extends AuthError {
  constructor() {
    super('sessionExistsError')
  }
}

export class SessionRefreshRequiredError extends AuthError {
  constructor() {
    super('sessionRefreshRequiredError')
  }
}

export function isAuthError(error: unknown): error is AuthError {
  return error instanceof AuthError
}
